// Centralized SEO + structured-data constants for Lone Star Counsel.
// Used by /src/components/SEO.jsx and any JSON-LD builder.

export const SITE = {
  name: "Lone Star Counsel",
  legalName: "Pratt Law Group, PLLC",
  alternateName: "Lone Star Counsel",
  publisher: "Pratt Law Group, PLLC d/b/a Lone Star Counsel",
  url: "https://lonestarcounsel.com",
  // Branded case for display anywhere the URL is shown as visible text.
  // Canonical URL / hrefs stay lowercase; UI copy uses this.
  displayUrl: "LoneStarCounsel.com",
  // Social-card image — branded 1200x630 bronze-star seal card. Used by
  // Facebook / Twitter / LinkedIn / Slack / iMessage previews. Absolute URL.
  ogImage: "https://lonestarcounsel.com/og/lone-star-counsel-og.jpg",
  ogImageSquare: "https://lonestarcounsel.com/og/lone-star-counsel-og-square.jpg",
  logo: "https://customer-assets.emergentagent.com/job_ralph-lauren-legal/artifacts/sufm7vsz_IMG_3406.png",
  twitter: "@lonestarcounsel",
  locale: "en_US",
  founder: "Darryl V. Pratt",
  founderRole: "Managing Partner — Attorney, CPA, Business Advisor",
  tagline: "Attorney. CPA. Business Advisor.",
  motto: "Planning Today. Protecting Tomorrow.\u00ae",

  // ISO date used as dateModified across pages. Bump on meaningful releases.
  lastUpdated: "2026-06-21",

  // Polished positioning paragraph — used as meta-description fallback,
  // JSON-LD `description`, and AI-citation source. Provided by the firm.
  description:
    "Lone Star Counsel is a Texas law firm advising entrepreneurs, business owners, professionals, high-net-worth individuals, and families through every stage of the business and wealth lifecycle — from business formation and growth to financial restructuring, mergers and acquisitions, business succession planning, trust and estate planning, probate administration, and legacy preservation. Build It. Grow It. Protect It. Transition It. Preserve It.",

  shortDescription:
    "Texas Business, Wealth & Legacy Counsel. Build It. Grow It. Protect It. Transition It. Preserve It.",

  // Pulled into Organization JSON-LD `areaServed`. Comprehensive — every
  // major Texas market + all DFW Metroplex suburbs — so Google Local &
  // AI-search index our reach across the state.
  areasServed: [
    "Texas",
    "State of Texas",

    // ---- Dallas–Fort Worth Metroplex (comprehensive) ----
    "Dallas–Fort Worth Metroplex", "DFW Metroplex",
    "Dallas", "Fort Worth", "Arlington", "Plano", "Frisco", "McKinney",
    "Irving", "Garland", "Grand Prairie", "Mesquite", "Richardson",
    "Carrollton", "Lewisville", "Denton", "Allen", "Flower Mound",
    "Mansfield", "Rowlett", "Euless", "Bedford", "Hurst", "Grapevine",
    "Cedar Hill", "Wylie", "Coppell", "Keller", "Roanoke", "Southlake",
    "Colleyville", "Highland Village", "Trophy Club", "Little Elm",
    "The Colony", "Prosper", "Celina", "Fairview", "Lucas", "Melissa",
    "Anna", "Princeton", "Farmersville", "Rockwall", "Heath", "Sachse",
    "Murphy", "Parker", "Lavon", "University Park", "Highland Park",
    "Duncanville", "Balch Springs", "Seagoville", "Lancaster",
    "Red Oak", "Waxahachie", "Ennis", "Midlothian", "Cleburne",
    "Burleson", "Crowley", "Aledo", "Weatherford", "Granbury",
    "Willow Park", "Azle", "Saginaw", "Watauga", "North Richland Hills",
    "Haltom City", "Kennedale", "Forney", "Terrell", "Sunnyvale",
    "Addison", "Farmers Branch",

    // ---- Greater Houston ----
    "Houston", "The Woodlands", "Sugar Land", "Katy", "Pearland",
    "Baytown", "Pasadena", "League City", "Missouri City", "Galveston",
    "Conroe", "Cypress", "Spring", "Humble", "Kingwood", "Tomball",
    "Friendswood", "Deer Park", "La Porte", "Rosenberg", "Richmond",
    "Bellaire", "West University Place", "Stafford", "Fulshear",
    "Cinco Ranch", "Atascocita", "Fort Bend County", "Harris County",

    // ---- Greater Austin ----
    "Austin", "Round Rock", "Cedar Park", "Georgetown", "Pflugerville",
    "Leander", "Kyle", "Buda", "San Marcos", "Lakeway", "Bee Cave",
    "Dripping Springs", "Hutto", "Manor", "Bastrop", "Elgin",
    "Wimberley", "Taylor",

    // ---- Greater San Antonio ----
    "San Antonio", "New Braunfels", "Schertz", "Cibolo", "Selma",
    "Live Oak", "Universal City", "Converse", "Boerne", "Kerrville",
    "Seguin", "Fair Oaks Ranch", "Fredericksburg", "Bulverde",

    // ---- Coastal Bend & South Texas ----
    "Corpus Christi", "Portland", "Kingsville", "Alice",
    "Brownsville", "McAllen", "Harlingen", "Edinburg", "Mission",
    "Weslaco", "Pharr", "San Benito", "Los Fresnos",
    "Rio Grande City", "Laredo",

    // ---- West Texas & Permian Basin ----
    "El Paso", "Socorro", "Horizon City",
    "Midland", "Odessa", "Big Spring",
    "Lubbock", "Wolfforth", "Amarillo",
    "Abilene", "San Angelo", "Sweetwater",

    // ---- East Texas ----
    "Tyler", "Longview", "Marshall", "Nacogdoches", "Lufkin",
    "Palestine", "Athens", "Kilgore", "Henderson", "Jacksonville",

    // ---- North Texas / Grayson & Fannin ----
    "Sherman", "Denison", "Gainesville", "Bonham", "Paris",

    // ---- Central Texas ----
    "Waco", "Temple", "Killeen", "Belton", "Copperas Cove",
    "Harker Heights", "Bryan", "College Station",
  ],

  sameAs: [
    // Placeholders — replace with real social handles when published.
    "https://www.linkedin.com/company/lone-star-counsel",
  ],

  // E-E-A-T signals for AEO — appear in JSON-LD Organization node.
  foundingDate: "2013",
  numberOfEmployees: 1,           // Boutique signal for AI overviews
  slogan: "Attorney. CPA. Business Advisor.",
  keywords: [
    "Texas business attorney",
    "Frisco Texas lawyer",
    "Dallas business law firm",
    "Chapter 11 Subchapter V attorney Texas",
    "Chapter 7 bankruptcy Texas",
    "Texas estate planning attorney",
    "Dynasty trust attorney Texas",
    "Buy-sell agreement lawyer",
    "Physician practice attorney Texas",
    "Franchise attorney Texas",
    "Indian family attorney Texas",
    "Indian and South Asian family attorney Texas",
    "South Asian attorney Texas",
    "NRI estate planning attorney",
    "High net worth attorney Texas",
    "Boutique law firm Frisco",
    "Attorney CPA Texas",
    // Metro-level SEO keywords — every major Texas market
    "Business attorney Dallas–Fort Worth",
    "Estate planning attorney Houston",
    "Business attorney Austin",
    "Trust attorney San Antonio",
    "Probate attorney El Paso",
    "Business attorney Corpus Christi",
    "Attorney Brownsville Harlingen",
    "Business attorney McAllen Edinburg",
    "Attorney Midland Odessa Permian Basin",
    "Business attorney Lubbock Amarillo",
    "Attorney Abilene San Angelo",
    "Business attorney Tyler Longview East Texas",
    "Attorney Waco Temple Killeen",
    "Attorney Bryan College Station",
    "Attorney Sherman Denison Grayson County",
    "Attorney The Woodlands Sugar Land Katy",
    "Attorney Round Rock Cedar Park Georgetown",
    "Attorney New Braunfels Boerne Kerrville",
    "Business succession attorney Texas Metroplex",
  ],
  keywordsAreas: [], // computed below
};
SITE.keywordsAreas = SITE.areasServed.map((c) => `${c} business attorney`);
